import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { normalizeOperadoras } from '@gestor-web/@core/utils';
import { environment } from '@gestor-web/environments/environment';
import { map } from 'rxjs/operators';
import { Api } from '../api';
import {
  ApiResponse,
  Benchmarking,
  ComparativoTissDiops,
  HistoricoIdss,
  ModeloPublicadoAns,
  Operadora,
  OperadoraPerfil,
  PontuacaoDimensao,
  RankingAns,
  RazaoTissDiops,
  ResultadoTree,
  UlitimoAnoBaseOficial,
} from '../ords/model';

const MODULE_PATH = '/idss-ans';

@Injectable({ providedIn: 'root' })
export class IdssAnsGoApi extends Api {
  constructor(http: HttpClient) {
    super(`${environment.API_GO_BASE_PATH}${MODULE_PATH}`, http);
  }

  /**
   * Busca os modelos publicados pela ANS das operadoras
   */
  fetchModelosPublicadosOperadoras({ ...params }: { ib_buscar_nao_vigente?: number; tp_programa_ans?: number }) {
    params = this.trimParams(params);
    return this.get<ApiResponse<ModeloPublicadoAns>>(`/modelos-publicados`, params);
  }

  /**
   * Busca o histórico do IDSS de uma operadora.
   *
   * @param regans código da operadora
   */
  fetchHistoricoIdss(regans: string) {
    if (!regans) {
      throw new Error('Required parameter regans was null when calling fetchHistoricoIdss.');
    }
    return this.get<ApiResponse<HistoricoIdss>>(`/historico-idss/${regans}`);
  }

  /**
   * Busca as pontuções das dimensões do IDSS de uma operadora.
   *
   * @param regans código da operadora
   */
  fetchPontuacaoDimensoes(regans: string) {
    if (!regans) {
      throw new Error('Required parameter regans was null when calling fetchHistoricoIdss.');
    }
    return this.get<ApiResponse<PontuacaoDimensao>>(`/pontuacao-dimensao/${regans}`);
  }

  /**
   * Busca o benchmarking de várias operadoras.
   *
   * @param regansList array com os códigos das operadoras
   */
  fetchBenchmarkIdss(regansList: string[], ano_base: number) {
    return this.get<ApiResponse<Benchmarking>>(`/bench-idss`, {
      regans_compare_list: regansList.join(','),
      ano_base,
    });
  }

  /**
   * Busca a dimensão do benchmarking de várias operadoras.
   *
   * @param regansList array com os códigos das operadoras
   */
  fetchBenchmarkDimensoes(regansList: string[], ano_base: number) {
    return this.get<ApiResponse<Benchmarking>>(`/bench-dimensao`, {
      regans_compare_list: regansList.join(','),
      ano_base,
    });
  }

  fetchUltimoAnoBaseOficial() {
    return this.get<ApiResponse<UlitimoAnoBaseOficial>>(`/bench/ultimo-ano-base`);
  }

  /**
   * Busca o ranking das operadoras
   *
   * @param anoBase
   */
  fetchRankingOperadora(anoBase: number) {
    return this.get<ApiResponse<RankingAns>>(`/ranking/${anoBase}`);
  }

  fetchRazaoTissDiops(regans: string) {
    return this.get<ApiResponse<RazaoTissDiops>>(`/razao-tiss-dtiss/${regans}`);
  }

  fetchDespesaAssistencialDiops(regans: string, nr_ano_base: number) {
    return this.get<ApiResponse<ComparativoTissDiops>>(`/diops/despesa-assistencial/${regans}`, { nr_ano_base });
  }

  /**
   * Recupera a lista de operadoras
   *
   * @param params lista de parâmetros opcionais
   *
   * cliente:
   *  - 1 para operadoras clientes
   *  - 0 para operadoras não clientes
   */
  fetchOperadoras(params?: { cliente?: 0 | 1 }) {
    return this.get<ApiResponse<Operadora>>('/operadoras', params).pipe(
      map(response => {
        response.items = normalizeOperadoras(response.items);
        return response;
      }),
    );
  }

  /**
   * Busca o histórico(?) do IDSS de uma operadora.
   *
   * @param regans código da operadora
   */
  fetchResultadoTree(regans: string) {
    if (!regans) {
      throw new Error('Required parameter regans was null when calling fetchHistoricoIdss.');
    }
    return this.get<ApiResponse<ResultadoTree>>(`/resultadotree/${regans}`);
  }

  fetchPerfilOperadora(regans: string) {
    return this.get<ApiResponse<OperadoraPerfil>>(`/perfil-operadora/${regans}`);
  }
}
